import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionLabel } from "@/components/Eyebrow";
import { IMG, TIER_META } from "@/lib/images";
import { api } from "@/lib/api";

const fadeUp = { initial: { opacity: 0, y: 14 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

const TRUST = ["Harvard ALM '17 · History & Government", "Former Harvard Admissions Interviewer", "Harvard Club of Central Florida", "University of Florida graduate", "Former Valencia College student", "Thousands of hours advising Florida transfer students"];

const PILLARS = [
  { n: "I", t: "Major Strategy", b: "Choosing the correct major early; nothing downstream works without it." },
  { n: "II", t: "Prerequisite Engineering", b: "Every semester planned backwards from the intended university's Common Prerequisites Manual entry." },
  { n: "III", t: "GPA Management", b: "Treat GPA like an investment portfolio. Every course has consequences." },
  { n: "IV", t: "Credit Protection", b: "Avoid unnecessary coursework. Prevent excess credits. Protect AA articulation." },
  { n: "V", t: "Application Strategy", b: "Timing. Departmental review. Supporting documents. School-specific positioning." },
  { n: "VI", t: "Academic Narrative", b: "Leadership, research, faculty relationships, career vision, essays." },
];

const PLAYBOOKS = [
  { id: "uf", title: "UF", sub: "University of Florida", body: "Limited access. Departmental review. One-major policy. Prereqs completed before enrollment.", to: "/playbooks/uf", image: TIER_META.florida_transfer.image },
  { id: "ucf", title: "UCF DirectConnect", sub: "Guaranteed AA articulation", body: "Guaranteed admission with AA from partner colleges. 45-credit checkpoint. myKnight Audit.", to: "/playbooks/ucf-directconnect", image: TIER_META.platform.image },
  { id: "harvard", title: "Harvard", sub: "Selective transfer", body: "Fall only. 1-2 years full-time. SAT/ACT. An academic-necessity thesis — not just performance.", to: "/playbooks/harvard", image: TIER_META.selective_elite.image },
];

const TOOLS = [
  { name: "Transfer Eligibility Checker", to: "/tools/eligibility",            blurb: "Verify articulation, prereq status, and GPA thresholds in one read." },
  { name: "GPA Forecast",                 to: "/tools/gpa-forecast",           blurb: "Project term-by-term GPA outcomes against your target university's competitive band." },
  { name: "Semester Planner",             to: "/tools/semester-planner",       blurb: "Auto-sequence prerequisites against the Common Prerequisites Manual." },
  { name: "Transfer Probability Dashboard", to: "/tools/probability-dashboard", blurb: "Categorical readiness signals across the six pillars." },
  { name: "Deadline Tracker",             to: "/tools/deadlines",              blurb: "Every prereq, milestone, and application date — surfaced before it matters." },
  { name: "Transcript Audit",             to: "/tools/transcript-audit",       blurb: "Upload a PDF transcript and get a full structured audit in under a minute." },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);
  useEffect(() => { (async () => {
    try { const [t, b] = await Promise.all([api.get("/testimonials"), api.get("/blog/posts?limit=2")]); setTestimonials(t.data || []); setPosts(b.data || []); } catch (e) {}
  })(); }, []);

  return (
    <Layout>
      <SEO
        title="Beyond Valencia | Florida Transfer Admissions — Valencia → UF → Harvard"
        description="Elite transfer admissions strategy for Florida community college students. Valencia → UF, UCF, FSU, Harvard. Founded by Taimur Ahmed, Harvard ALM '17."
        canonical="/"
        keywords="Florida transfer, Valencia College, UF transfer, UCF DirectConnect, Harvard transfer, community college to Ivy League, admissions consulting"
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-ink" data-testid="home-hero">
        <div className="absolute inset-0 bg-cover bg-center opacity-60" style={{ backgroundImage: `url('${IMG.heroHome}')` }} />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-24 sm:pt-36 sm:pb-32 lg:pt-44 lg:pb-40">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <Eyebrow tone="bone" className="text-bone-dim">The Transfer Intelligence Platform</Eyebrow>
              <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[5.25rem] text-bone leading-[1.02] tracking-tightest mt-6">
                Your starting point is not<br /><span className="italic text-brass-soft">your ceiling.</span>
              </h1>
              <p className="mt-8 max-w-xl text-base sm:text-lg text-bone-dim leading-relaxed">
                Beyond Valencia is the national platform for transfer strategy, educational mobility, and second-chance admissions.
              </p>
              <Link
                to="/partners/ivy-league-consulting"
                className="mt-6 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] text-brass-soft hover:text-brass font-semibold border-l-2 border-brass pl-3 py-1"
                data-testid="home-hero-partnership-marker"
              >
                In partnership with <span className="font-serif normal-case tracking-normal text-sm text-bone">Ivy League Consulting</span>
              </Link>
            </div>
            <div className="lg:col-span-4 lg:border-l border-bone-dim/20 lg:pl-8 flex flex-col justify-end space-y-4">
              <Link to="/build-my-plan" className="block w-full bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 text-center transition-colors" data-testid="hero-cta-build-plan">Build your transfer plan →</Link>
              <Link to="/mishap-corrector" className="block w-full border border-bone-dim/40 hover:border-bone text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 text-center transition-colors" data-testid="hero-cta-mishap">Fix your transfer path →</Link>
              <Link to="/neo" className="block w-full border border-brass/50 hover:border-brass hover:bg-brass/10 text-brass-soft hover:text-brass uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 text-center transition-colors" data-testid="hero-cta-neo">Ask NEO — AI Navigator</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHASE N — Three ideas we're built on */}
      <section className="bg-bone border-b border-stone/30" data-testid="home-3-ideas">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Three ideas we're built on</div>
          <div className="mt-8 grid md:grid-cols-3 gap-0 border-t border-l border-stone/40">
            {[
              { n: "I",   h: "Your starting point is not your ceiling.",                                          b: "Where you begin as a college student should never determine where you finish. Beyond Valencia exists to make that mobility real, measurable, and repeatable." },
              { n: "II",  h: "Community college is a strategic pathway.",                                         b: "Community college can be one of the smartest strategic pathways in American higher education. Cost, credit portability, and the transfer articulation system are underused advantages." },
              { n: "III", h: "Ambitious guidance regardless of where you begin.",                                 b: "Ambitious students deserve ambitious guidance regardless of where they begin. The intelligence, the data, and the strategy should be available to everyone — not gated by zip code or family history with elite admissions." },
            ].map((c) => (
              <div key={c.n} className="border-r border-b border-stone/40 bg-bone p-6 sm:p-7" data-testid={`home-idea-${c.n.toLowerCase()}`}>
                <div className="font-serif text-brass text-2xl tabular-nums leading-none">{c.n}.</div>
                <h3 className="mt-4 font-serif text-xl text-ivy tracking-tight leading-snug">{c.h}</h3>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHASE F TEASER — Mishap Corrector */}
      <section className="bg-bone border-b border-stone/30" data-testid="home-mishap-teaser">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">New · Free · No signup required</div>
            <h2 className="mt-4 font-serif font-medium text-3xl sm:text-4xl lg:text-5xl text-ink tracking-tight leading-[1.1]">
              Fix your transfer path in <span className="text-ivy">three minutes</span>.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-ink-soft max-w-2xl leading-relaxed">
              Upload your Valencia transcript. We check every course against the official UF Dietetics prereqs — with sources on every claim — and give you the exact term-by-term correction.
            </p>
            <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-stone">POC · Valencia → UF Dietetics</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/mishap-corrector" className="bg-brass hover:bg-brass/90 text-ivy uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 inline-block" data-testid="home-mishap-cta">
              Run Mishap Corrector →
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-ivy text-bone border-b border-ivy-light" data-testid="home-trust-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-x-6 gap-y-3">
            {TRUST.map((t, i) => (
              <div key={i} className="flex items-start gap-2.5 text-[11px] uppercase tracking-[0.16em] text-bone-dim leading-tight">
                <span className="text-brass mt-0.5">·</span><span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORBES CREDIBILITY BAND */}
      <section className="bg-bone-dim border-b border-stone/30" data-testid="home-forbes-band">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 lg:py-14">
          <Eyebrow>Recognition</Eyebrow>
          <p className="mt-4 font-serif font-medium text-xl sm:text-2xl lg:text-[1.75rem] text-ivy leading-snug tracking-tight">
            The University of Florida is a <span className="border-b-2 border-brass">2026 Forbes New Ivy</span>. Our flagship transfer destination is now on every employer&rsquo;s short list.
          </p>
          <p className="mt-4 text-sm text-ink-soft max-w-3xl leading-relaxed">
            Florida&rsquo;s 2+2 system is the most overlooked pathway into elite, employer-friendly higher education in America.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <Link to="/resources/florida-aa-to-forbes-new-ivy" className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass transition-colors inline-flex items-center gap-1.5" data-testid="forbes-guide-link">Read the New Ivy guide <ArrowRight className="h-3 w-3" /></Link>
            <span className="text-[10px] uppercase tracking-[0.2em] text-stone">Forbes 2026 New Ivies · Alicia Park · April 8, 2026</span>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-bone border-b border-stone/30" data-testid="home-manifesto">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <SectionLabel index="01" label="The Method" />
          <p className="mt-5 font-serif font-medium text-2xl sm:text-3xl lg:text-[2.5rem] leading-[1.2] text-ink tracking-tight">
            We don&rsquo;t complete transfer applications. <span className="text-ivy italic">We engineer transfer success.</span>
          </p>
          <p className="mt-5 italic text-stone text-base sm:text-lg">Applications merely document two years of decisions. We manage the decisions.</p>
        </div>
      </section>

      {/* SIX PILLARS */}
      <section className="bg-bone" data-testid="home-six-pillars">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <motion.div {...fadeUp} className="max-w-3xl">
            <SectionLabel index="02" label="The Six Pillars" />
            <h2 className="font-serif font-medium text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight tracking-tight mt-5">
              Transfer admission is won during the first semester, not the last.
            </h2>
          </motion.div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-stone/40">
            {PILLARS.map((p, i) => (
              <motion.div key={p.n} {...fadeUp} transition={{ duration: 0.55, delay: i * 0.06 }} className="border-r border-b border-stone/40 p-8 lg:p-10 bg-bone">
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="font-serif text-brass text-3xl leading-none">{p.n}</span>
                  <span className="h-px flex-1 bg-stone/40" />
                </div>
                <h3 className="font-serif font-medium text-xl text-ink tracking-tight uppercase">{p.t}</h3>
                <p className="text-sm text-ink-soft mt-3 leading-relaxed">{p.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="bg-bone-dim border-y border-stone/30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24 lg:py-28">
          <SectionLabel index="03" label="For Whom" />
          <div className="mt-10 grid md:grid-cols-3 gap-0 border-t border-l border-stone/40">
            {[
              { h: "Florida high school seniors", b: "Valencia, Seminole, Santa Fe, Daytona, Eastern Florida, Lake Sumter, Broward, Miami Dade, Palm Beach, Polk — and parents." },
              { h: "Current community college students", b: "First semester through last." },
              { h: "Students who have already made mistakes", b: "Wrong major, wrong electives, low GPA, excess credits, failed prereqs, late planning. Especially you." },
            ].map((a) => (
              <div key={a.h} className="border-r border-b border-stone/40 bg-bone p-8 lg:p-10">
                <h3 className="font-serif font-medium text-xl text-ink leading-tight tracking-tight">{a.h}</h3>
                <p className="text-sm text-ink-soft mt-4 leading-relaxed">{a.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLAYBOOKS TEASER */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <motion.div {...fadeUp} className="max-w-3xl">
          <SectionLabel index="04" label="The Playbooks" />
          <h2 className="font-serif font-medium text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight tracking-tight mt-5">
            Three universities. <span className="italic text-ivy">Three completely different games.</span>
          </h2>
        </motion.div>
        <div className="mt-12 grid md:grid-cols-3 gap-0 border-t border-l border-stone/40">
          {PLAYBOOKS.map((p, i) => (
            <motion.div key={p.id} {...fadeUp} transition={{ duration: 0.55, delay: i * 0.08 }} className="border-r border-b border-stone/40 bg-bone flex flex-col" data-testid={`home-playbook-${p.id}`}>
              <div className="aspect-[16/10] overflow-hidden border-b border-stone/40">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-7 flex-1 flex flex-col">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brass">{p.sub}</div>
                <h3 className="font-serif font-medium text-2xl text-ink mt-2 tracking-tight">{p.title}</h3>
                <p className="text-sm text-ink-soft mt-4 leading-relaxed flex-1">{p.body}</p>
                <Link to={p.to} className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass transition-colors">Read the playbook <ArrowRight className="h-3 w-3" /></Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TRANSFER AUDIT CTA */}
      <section className="bg-ivy text-bone border-y border-ivy-light" data-testid="home-audit-cta">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-24 lg:py-32 text-center">
          <Eyebrow tone="bone" className="text-brass-soft">Start Here</Eyebrow>
          <h2 className="mt-5 font-serif font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">Unsure where you stand?</h2>
          <p className="mt-6 text-base sm:text-lg text-bone-dim/90 leading-relaxed max-w-2xl mx-auto">
            We will review your transcript, evaluate your target schools, identify prerequisite gaps, and build a semester-by-semester transfer strategy tailored to your goals.
          </p>
          <Link to="/transfer-audit" className="inline-block mt-10 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors" data-testid="home-audit-cta-btn">
            Schedule a Transfer Audit — $249
          </Link>
        </div>
      </section>

      {/* TOOLS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <SectionLabel index="06" label="Field Instruments" />
        <h2 className="font-serif font-medium text-3xl sm:text-4xl text-ink leading-tight tracking-tight mt-5 max-w-2xl">Six AI tools. Live.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-stone/40">
          {TOOLS.map((t) => (
            <Link key={t.name} to={t.to} className="border-r border-b border-stone/40 bg-bone p-6 flex flex-col hover:bg-bone-dim transition-colors group">
              <span className="text-[10px] uppercase tracking-[0.22em] text-ivy font-semibold">Live</span>
              <h3 className="font-serif text-xl text-ink mt-3 tracking-tight group-hover:text-ivy transition-colors">{t.name}</h3>
              <p className="text-sm text-ink-soft mt-3 leading-relaxed flex-1">{t.blurb}</p>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/tools" className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass">See all tools →</Link>
        </div>
      </section>

      {/* Tools teaser → links to /playbook */}
      <section className="bg-bone-dim border-y border-stone/30" data-testid="home-from-founder">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <SectionLabel index="07" label="From the Founder" />
          <h2 className="font-serif font-medium text-3xl sm:text-4xl text-ink leading-tight tracking-tight mt-5 max-w-3xl">The New Ivy Playbook.</h2>
          <p className="mt-5 text-ink-soft text-base sm:text-lg max-w-2xl leading-relaxed">A 10-article series by Taimur Ahmed. Two published. Eight forthcoming.</p>
          <div className="mt-12 grid md:grid-cols-3 gap-0 border-t border-l border-stone/40">
            {[
              { n: "I", title: "The Ivy League Isn't the Only Road to Success Anymore", to: "/blog/ivy-league-isnt-the-only-road", excerpt: "Forbes named UF a 2026 New Ivy. Here's why employers stopped recruiting only at Harvard." },
              { n: "II", title: "The New Rules of College Admissions in the Age of AI", to: "/blog/new-rules-college-admissions-ai", excerpt: "AI didn't make college less important. It made choosing the right college vastly more important." },
              { n: "·", title: "Your Most Underrated Admissions Tool: Google NotebookLM", to: "/blog/notebooklm-admissions-tool", excerpt: "Most students think of AI as something that writes essays. That's selling the technology incredibly short." },
            ].map((p) => (
              <Link key={p.to} to={p.to} className="border-r border-b border-stone/40 bg-bone p-7 flex flex-col group hover:bg-bone-dim transition-colors" data-testid={`home-founder-card-${p.n.toLowerCase()}`}>
                <span className="font-serif text-brass text-3xl leading-none">{p.n}</span>
                <h3 className="font-serif font-medium text-xl text-ink mt-5 tracking-tight leading-snug group-hover:text-ivy transition-colors">{p.title}</h3>
                <p className="text-sm text-ink-soft mt-3 leading-relaxed flex-1">{p.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy border-b border-brass pb-0.5 w-fit">Read <ArrowRight className="h-3 w-3" /></div>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/playbook" className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass" data-testid="home-playbook-cta">See the full playbook →</Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-ivy text-bone">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
            <SectionLabel index="07" label="In their words" tone="bone" />
            <div className="mt-12 grid md:grid-cols-3 gap-0 border-t border-l border-bone-dim/15">
              {testimonials.map((t) => (
                <figure key={t.id} className="border-r border-b border-bone-dim/15 p-8">
                  <span className="font-serif text-brass-soft text-4xl leading-none block mb-4">&ldquo;</span>
                  <blockquote className="font-serif text-lg leading-snug text-bone tracking-tight">{t.quote}</blockquote>
                  <figcaption className="mt-6 pt-4 border-t border-bone-dim/15 text-sm">
                    <div className="font-medium text-bone">{t.name}</div>
                    <div className="text-bone-dim/60 text-[10px] uppercase tracking-[0.22em] mt-1">{t.attribution}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BLOG TEASER */}
      {posts.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <SectionLabel index="08" label="Field Notes" />
          <div className="mt-10 grid md:grid-cols-2 gap-0 border-t border-l border-stone/40">
            {posts.map((p) => (
              <article key={p.slug} className="border-r border-b border-stone/40 bg-bone">
                <Link to={`/blog/${p.slug}`} className="block group">
                  {p.hero_image_url && <div className="aspect-[16/9] overflow-hidden border-b border-stone/40"><img src={p.hero_image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" /></div>}
                  <div className="p-7">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-brass">{new Date(p.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</div>
                    <h3 className="font-serif font-medium text-2xl text-ink mt-3 leading-tight tracking-tight group-hover:text-ivy transition-colors">{p.title}</h3>
                    <p className="text-sm text-ink-soft mt-3 leading-relaxed">{p.excerpt}</p>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}
